import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, FileText, MessageSquare, Search, BookOpen, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';

const Home = () => {
  const features = [
    {
      icon: <BookOpen className="h-8 w-8" />,
      title: 'Understanding the Legislature',
      description: 'Learn about the structure, roles, and functions of the Nigerian Legislature',
      link: '/legislature',
      color: 'bg-blue-50 text-blue-700'
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: 'Find My Representative',
      description: 'Locate your federal representative by state and constituency',
      link: '/representatives',
      color: 'bg-emerald-50 text-emerald-700'
    },
    {
      icon: <MessageSquare className="h-8 w-8" />,
      title: 'Engagement Tools',
      description: 'Step-by-step guides for petitions, hearings, and civic participation',
      link: '/engagement-tools',
      color: 'bg-purple-50 text-purple-700'
    },
    {
      icon: <FileText className="h-8 w-8" />,
      title: 'Templates',
      description: 'Download petition, memorandum, and testimony templates',
      link: '/templates',
      color: 'bg-amber-50 text-amber-700'
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: 'Engagement Ladder',
      description: 'Understand the levels of citizen engagement in legislation',
      link: '/engagement-ladder',
      color: 'bg-pink-50 text-pink-700'
    },
    {
      icon: <Search className="h-8 w-8" />,
      title: 'FAQs',
      description: 'Get answers to frequently asked questions about the National Assembly',
      link: '/faqs',
      color: 'bg-indigo-50 text-indigo-700'
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-900 via-primary-800 to-emerald-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1604212561903-5ca7f041c58b?crop=entropy&cs=srgb&fm=jpg&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight mb-6">
              Empowering Citizens to Engage with Nigeria's Legislature
            </h1>
            <p className="text-lg md:text-xl text-emerald-100 leading-relaxed mb-8">
              Access tools, information, and resources to participate actively in the democratic process. 
              Connect with your representatives and make your voice heard.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/representatives">
                <Button 
                  size="lg" 
                  className="bg-accent hover:bg-accent-hover text-white rounded-full px-8 py-6 text-lg font-medium transition-transform active:scale-95 w-full sm:w-auto"
                  data-testid="find-representative-cta"
                >
                  Find My Representative
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/engagement-tools">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-primary-900 rounded-full px-8 py-6 text-lg font-medium w-full sm:w-auto"
                  data-testid="engagement-tools-cta"
                >
                  Explore Engagement Tools
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Search Section */}
      <section className="bg-surface py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1">
                <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                  Looking for something specific?
                </h2>
                <p className="text-slate-600">
                  Search across representatives, bills, FAQs, and resources
                </p>
              </div>
              <Link to="/search">
                <Button 
                  size="lg"
                  className="bg-primary-700 hover:bg-primary-900 text-white rounded-full px-8"
                  data-testid="search-cta"
                >
                  <Search className="mr-2 h-5 w-5" />
                  Search Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              What You Can Do
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Explore our comprehensive toolkit designed to make civic engagement simple and accessible
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Link to={feature.link}>
                  <div 
                    className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 h-full"
                    data-testid={`feature-card-${index}`}
                  >
                    <div className={`w-16 h-16 ${feature.color} rounded-xl flex items-center justify-center mb-4`}>
                      {feature.icon}
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed mb-4">
                      {feature.description}
                    </p>
                    <div className="flex items-center text-primary-700 font-medium text-sm">
                      Learn more
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
            Your Voice Matters
          </h2>
          <p className="text-lg text-emerald-100 mb-8 max-w-2xl mx-auto">
            Active citizen participation makes laws more responsive to community needs. 
            Start engaging with your representatives today.
          </p>
          <Link to="/legislature">
            <Button 
              size="lg"
              className="bg-white text-primary-900 hover:bg-emerald-50 rounded-full px-8 py-6 text-lg font-medium"
              data-testid="get-started-cta"
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
